import { useState } from "react";
import { 
  ArrowLeft, 
  Truck, 
  User, 
  Layers, 
  Search,
  Printer,
  Split,
  CreditCard,
  Send,
  ArrowRightLeft,
  XCircle,
  ClipboardList,
  Tag
} from "lucide-react";
import { OrderSummaryDialog } from "./OrderSummaryDialog";
import { Button } from "@/components/ui/button";

interface PDVActionBarProps {
  subtotal: number;
  serviceTax: number;
  serviceTaxPercentage: number;
  entryAmount: number | null;
  entryTypeName?: string;
  total: number;
  onBack: () => void;
  onCheckout: () => void;
  onSendItems: () => void;
  onSwitchToDelivery?: () => void;
  onCustomerData?: () => void;
  onSetNickname?: () => void;
  currentNickname?: string | null;
  showNicknameButton?: boolean;
  onGroupTickets?: () => void;
  onConsultSales?: () => void;
  onPrint?: () => void;
  onSplitBill?: () => void;
  onTransferItems?: () => void;
  onCancelOrder?: () => void;
  onViewDetails?: () => void;
  itemCount: number;
  newItemCount: number;
  pendingCancellationsCount?: number;
  isSending?: boolean;
  showKeyboardShortcuts?: boolean;
  hasExistingOrder?: boolean;
  hideSendButton?: boolean;
}

export function PDVActionBar({
  subtotal,
  serviceTax,
  serviceTaxPercentage,
  entryAmount,
  entryTypeName,
  total,
  onBack,
  onCheckout,
  onSendItems,
  onSwitchToDelivery,
  onCustomerData,
  onSetNickname,
  currentNickname,
  showNicknameButton = false,
  onGroupTickets,
  onConsultSales,
  onPrint,
  onSplitBill,
  onTransferItems,
  onCancelOrder,
  onViewDetails,
  itemCount,
  newItemCount,
  pendingCancellationsCount = 0,
  isSending = false,
  showKeyboardShortcuts = false,
  hasExistingOrder = false,
  hideSendButton = false,
}: PDVActionBarProps) {
  const [showSummary, setShowSummary] = useState(false);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  return (
    <div className="border-t border-[var(--pdv-border,#1e2d52)] bg-[var(--pdv-surface-panel,#0b1020)] shrink-0">
      {/* Totals Row */}
      <div className="flex items-center px-3 py-1.5 border-b border-[var(--pdv-border,#1e2d52)] gap-3 overflow-x-auto">
        {/* Total - Clickable */}
        <button
          onClick={() => setShowSummary(true)}
          className="shrink-0 bg-[var(--pdv-success-bg,#0a3d1e)] px-3 py-1 rounded-lg flex items-center gap-1.5 hover:opacity-90 transition-colors cursor-pointer border border-[var(--pdv-success-border,#00c853)]/30"
          title="Clique para ver resumo detalhado"
        >
          <span className="text-xs text-[var(--pdv-text-subtle,#4a7abf)]">Total:</span>
          <span className="text-[16px] font-extrabold text-[var(--pdv-success,#00e676)]">{formatCurrency(total)}</span>
        </button>

        <div className="h-5 w-px bg-[var(--pdv-border,#1e2d52)] shrink-0" />

        <div className="flex items-center gap-3 overflow-x-auto min-w-0 flex-1">
          <div className="flex items-center gap-1 shrink-0">
            <span className="text-xs text-[var(--pdv-text-subtle,#4a7abf)]">Produtos:</span>
            <span className="text-sm font-medium text-[var(--pdv-text,#c8dff5)]">{formatCurrency(subtotal)}</span>
          </div>

          {serviceTax > 0 && (
            <>
              <span className="text-[var(--pdv-border)]/50">•</span>
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-xs text-[var(--pdv-accent,#00aaff)]">
                  Serviço ({serviceTaxPercentage}%):
                </span>
                <span className="text-sm font-medium text-[var(--pdv-accent,#00aaff)]">
                  {formatCurrency(serviceTax)}
                </span>
              </div>
            </>
          )}

          {entryAmount !== null && entryAmount > 0 && (
            <>
              <span className="text-[var(--pdv-border)]/50">•</span>
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-xs text-orange-400">
                  {entryTypeName || "Entrada"}:
                </span>
                <span className="text-sm font-medium text-orange-400">
                  {formatCurrency(entryAmount)}
                </span>
              </div>
            </>
          )}

          <span className="text-[var(--pdv-border)]/50">•</span>
          <div className="flex items-center gap-1 shrink-0">
            <span className="text-xs text-[var(--pdv-text-subtle,#4a7abf)]">Itens:</span>
            <span className="text-sm font-medium text-[var(--pdv-text,#c8dff5)]">{itemCount}</span>
          </div>
        </div>
      </div>

      {/* Actions Row */}
      <div className="flex items-center gap-1.5 p-2.5 px-3.5 overflow-x-auto">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 shrink-0 rounded-[10px] min-h-[44px] bg-[var(--pdv-surface,#111829)] border-[var(--pdv-border,#1e2d52)] text-[var(--pdv-text-muted,#7aabdd)] hover:bg-[var(--pdv-highlight-hover)] text-xs font-bold"
          onClick={onBack}
          title="Esc"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar
          {showKeyboardShortcuts && (
            <span className="text-[8px] text-[var(--pdv-text-subtle)]/40 hidden xl:inline">Esc</span>
          )}
        </Button>

        {!hideSendButton && (
          <Button
            size="sm"
            className="gap-1.5 shrink-0 rounded-[10px] min-h-[44px] bg-[var(--pdv-highlight,#0a3a6e)] border border-[var(--pdv-highlight-border,#0077cc)] text-[var(--pdv-accent-light,#4dd0ff)] hover:bg-[var(--pdv-highlight-border)]/80 text-xs font-bold"
            onClick={onSendItems}
            disabled={(newItemCount === 0 && pendingCancellationsCount === 0) || isSending}
            title="Ctrl+Enter"
          >
            <Send className="h-4 w-4" />
            {isSending ? "Salvando..." : (() => {
              const parts = [];
              if (newItemCount > 0) parts.push(`${newItemCount} ${newItemCount === 1 ? 'novo' : 'novos'}`);
              if (pendingCancellationsCount > 0) parts.push(`${pendingCancellationsCount} canc.`);
              return parts.length > 0 ? `Enviar (${parts.join(', ')})` : "Enviar";
            })()}
          </Button>
        )}

        <Button
          size="sm"
          className="gap-1.5 shrink-0 rounded-[10px] min-h-[44px] bg-[var(--pdv-success-bg,#0a3d1e)] border border-[var(--pdv-success-border,#00c853)] text-[var(--pdv-success,#00e676)] hover:bg-[var(--pdv-success-border)]/30 text-xs font-bold"
          onClick={onCheckout}
          disabled={itemCount === 0 && !hasExistingOrder}
          title="F12"
        >
          <CreditCard className="h-4 w-4" />
          Receber
        </Button>

        <div className="h-6 w-px bg-[var(--pdv-border,#1e2d52)] mx-0.5 shrink-0" />

        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 shrink-0 rounded-[10px] min-h-[44px] bg-[var(--pdv-surface,#111829)] border-[var(--pdv-border,#1e2d52)] text-[var(--pdv-text-muted,#7aabdd)] hover:bg-[var(--pdv-highlight-hover)] text-xs font-bold"
          onClick={onSwitchToDelivery}
        >
          <Truck className="h-4 w-4" />
          <span className="hidden lg:inline">Delivery</span>
        </Button>

        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 shrink-0 rounded-[10px] min-h-[44px] bg-[var(--pdv-surface,#111829)] border-[var(--pdv-border,#1e2d52)] text-[var(--pdv-text-muted,#7aabdd)] hover:bg-[var(--pdv-highlight-hover)] text-xs font-bold"
          onClick={onCustomerData}
        >
          <User className="h-4 w-4" />
          <span className="hidden lg:inline">Cliente</span>
        </Button>

        {showNicknameButton && (
          <Button
            variant="outline"
            size="sm"
            className={
              "gap-1.5 shrink-0 rounded-[10px] min-h-[44px] border-[var(--pdv-border,#1e2d52)] hover:bg-[var(--pdv-highlight-hover)] text-xs font-bold " +
              (currentNickname
                ? "bg-[var(--pdv-highlight,#0a3a6e)] text-[var(--pdv-accent-light,#4dd0ff)] border-[var(--pdv-highlight-border,#0077cc)]"
                : "bg-[var(--pdv-surface,#111829)] text-[var(--pdv-text-muted,#7aabdd)]")
            }
            onClick={onSetNickname}
            title="Apelido do cliente (sai na impressão)"
          >
            <Tag className="h-4 w-4" />
            <span className="hidden lg:inline truncate max-w-[140px]">
              {currentNickname ? `Apelido: ${currentNickname}` : "Apelido"}
            </span>
          </Button>
        )}

        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 shrink-0 rounded-[10px] min-h-[44px] bg-[var(--pdv-surface,#111829)] border-[var(--pdv-border,#1e2d52)] text-[var(--pdv-text-muted,#7aabdd)] hover:bg-[var(--pdv-highlight-hover)] text-xs font-bold"
          onClick={onGroupTickets}
        >
          <Layers className="h-4 w-4" />
          <span className="hidden lg:inline">Agrupar</span>
        </Button>

        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 shrink-0 rounded-[10px] min-h-[44px] bg-[var(--pdv-surface,#111829)] border-[var(--pdv-border,#1e2d52)] text-[var(--pdv-text-muted,#7aabdd)] hover:bg-[var(--pdv-highlight-hover)] text-xs font-bold"
          onClick={onSplitBill}
        >
          <Split className="h-4 w-4" />
          <span className="hidden lg:inline">Dividir</span>
        </Button>

        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 shrink-0 rounded-[10px] min-h-[44px] bg-[var(--pdv-surface,#111829)] border-[var(--pdv-border,#1e2d52)] text-[var(--pdv-text-muted,#7aabdd)] hover:bg-[var(--pdv-highlight-hover)] text-xs font-bold"
          onClick={onTransferItems}
        >
          <ArrowRightLeft className="h-4 w-4" />
          <span className="hidden lg:inline">Transferir</span>
        </Button>

        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 shrink-0 rounded-[10px] min-h-[44px] bg-red-950/50 border-red-800/50 text-red-400 hover:bg-red-900/50 text-xs font-bold"
          onClick={onCancelOrder}
        >
          <XCircle className="h-4 w-4" />
          <span className="hidden lg:inline">Cancelar Pedido</span>
        </Button>

        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 shrink-0 rounded-[10px] min-h-[44px] bg-[var(--pdv-surface,#111829)] border-[var(--pdv-border,#1e2d52)] text-[var(--pdv-text-muted,#7aabdd)] hover:bg-[var(--pdv-highlight-hover)] text-xs font-bold"
          onClick={onViewDetails}
          disabled={!hasExistingOrder}
        >
          <ClipboardList className="h-4 w-4" />
          <span className="hidden lg:inline">Detalhes</span>
        </Button>

        <div className="flex-1 min-w-2" />

        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 shrink-0 rounded-[10px] min-h-[44px] bg-[var(--pdv-surface,#111829)] border-[var(--pdv-border,#1e2d52)] text-[var(--pdv-text-muted,#7aabdd)] hover:bg-[var(--pdv-highlight-hover)] text-xs font-bold"
          onClick={onPrint}
        >
          <Printer className="h-4 w-4" />
          <span className="hidden lg:inline">Imprimir</span>
        </Button>

        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 shrink-0 rounded-[10px] min-h-[44px] bg-[var(--pdv-surface,#111829)] border-[var(--pdv-border,#1e2d52)] text-[var(--pdv-text-muted,#7aabdd)] hover:bg-[var(--pdv-highlight-hover)] text-xs font-bold"
          onClick={onConsultSales}
        >
          <Search className="h-4 w-4" />
          <span className="hidden lg:inline">Consultar</span>
        </Button>
      </div>

      {/* Modal de Resumo */}
      <OrderSummaryDialog
        open={showSummary}
        onOpenChange={setShowSummary}
        subtotal={subtotal}
        serviceTax={serviceTax}
        serviceTaxPercentage={serviceTaxPercentage}
        entryAmount={entryAmount}
        entryTypeName={entryTypeName}
        total={total}
        itemCount={itemCount}
      />
    </div>
  );
}
